import {Button, Group, Modal, Stack, Text} from '@mantine/core'
import {useForm} from '@mantine/form'
import {useEffect, useRef, useState} from 'react'

import {StepBillingTerms} from '../jobs/step-billing-terms'
import type {CreateProjectFormValues} from '../projects/create-project-form-values'
import {validateBillingTerms} from '../projects/create-project-validation'
import {
    buildProjectBillingTermsInput,
    projectBillingTermsToFormValues,
    toWizardFormValues,
} from '../projects/project-billing-terms-utils'
import type {ProjectBillingTerms} from '../projects/schemas'
import {useTransportationRatePolicy} from '../settings/use-transportation-rate-policy'
import {useReviseServiceScheduleBillingTerms} from './use-service-schedules'

type ScheduleBillingTermsModalProps = {
    opened: boolean
    onClose: () => void
    scheduleId: string
    terms: ProjectBillingTerms | null
}

/**
 * Edits a schedule's billing terms in place. Saving stamps a new immutable version onto the
 * schedule and its jobs that have no charges yet — the user never sees or picks a version.
 */
export function ScheduleBillingTermsModal({
    opened,
    onClose,
    scheduleId,
    terms,
}: ScheduleBillingTermsModalProps) {
    const reviseBillingTerms = useReviseServiceScheduleBillingTerms(scheduleId)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const {data: policy} = useTransportationRatePolicy()
    const activeMinimumRef = useRef<number | null>(null)
    activeMinimumRef.current = policy?.minimumHourlyRate ?? null

    const form = useForm<CreateProjectFormValues>({
        initialValues: toWizardFormValues(projectBillingTermsToFormValues(terms)),
        validate: (values) => validateBillingTerms(values, activeMinimumRef.current),
    })

    useEffect(() => {
        if (opened) {
            form.setValues(toWizardFormValues(projectBillingTermsToFormValues(terms)))
            setErrorMessage(null)
        }
    }, [opened, terms?.id])

    async function handleSave() {
        if (form.validate().hasErrors) {
            return
        }

        setErrorMessage(null)
        try {
            await reviseBillingTerms.mutateAsync(
                buildProjectBillingTermsInput(
                    {
                        billingMethod: form.values.billingMethod,
                        billingTerms: form.values.billingTerms,
                    },
                    terms,
                ),
            )
            onClose()
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Failed to save billing terms')
        }
    }

    return (
        <Modal opened={opened} onClose={onClose} title="Edit billing terms" size="lg">
            <Stack gap="md">
                <Text size="sm" c="dimmed">
                    These rates apply to this schedule and its visits that have not been billed yet.
                    Visits with existing charges keep the rates those charges were calculated under.
                </Text>

                {errorMessage ? (
                    <Text size="sm" c="red">
                        {errorMessage}
                    </Text>
                ) : null}

                <StepBillingTerms form={form} showManualCharges={false} />

                <Group justify="flex-end">
                    <Button
                        variant="default"
                        onClick={onClose}
                        disabled={reviseBillingTerms.isPending}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => void handleSave()}
                        loading={reviseBillingTerms.isPending}
                    >
                        Save
                    </Button>
                </Group>
            </Stack>
        </Modal>
    )
}
